import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, CheckCircle, AlertTriangle, X, Eye, ChevronLeft, ChevronRight,
  Download, Edit3, Save, Camera, User, Car, FileText, Hash, Calendar,
  Building2, FileCheck, Loader2, CheckCircle2, XCircle, Info, Lock, Gauge 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── PDF Generator (client-side using jsPDF) ─────────────────────────────────
const generateVerificationPDF = async (data, vehicleId, verificationId) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const pageW = 210;
  const margin = 15;
  let y = margin;

  const addText = (text, x, curY, size = 10, bold = false, color = [0, 0, 0]) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...color);
    doc.text(String(text || '—'), x, curY);
    return curY + (size * 0.5);
  };

  const addLine = (curY) => {
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, curY, pageW - margin, curY);
    return curY + 5;
  };

  const checkPage = (curY, needed = 20) => {
    if (curY + needed > 280) { doc.addPage(); return margin; }
    return curY;
  };

  // Header
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pageW, 35, 'F');
  y = addText('X7 AUTOZ — VEHICLE VERIFICATION REPORT', margin, 15, 16, true, [255, 255, 255]);
  y = addText(`Verification ID: ${verificationId || 'N/A'}`, margin, 23, 10, false, [200, 220, 255]);
  y = addText(`Generated: ${new Date().toLocaleString('en-IN')}`, margin, 30, 9, false, [180, 210, 255]);
  y = 45;

  // Section helper
  const addSection = (title, curY) => {
    curY = checkPage(curY, 15);
    doc.setFillColor(240, 245, 255);
    doc.rect(margin, curY - 4, pageW - margin * 2, 10, 'F');
    curY = addText(title, margin + 2, curY + 2, 11, true, [30, 64, 175]);
    return curY + 3;
  };

  const addRow = (label, value, curY, highlight = false) => {
    curY = checkPage(curY, 10);
    if (highlight) {
      doc.setFillColor(255, 251, 235);
      doc.rect(margin, curY - 4, pageW - margin * 2, 8, 'F');
    }
    addText(label + ':', margin + 2, curY, 9, true, [80, 80, 80]);
    addText(value, margin + 60, curY, 9, false, [20, 20, 20]);
    return curY + 7;
  };

  // Vehicle info
  y = addSection('VEHICLE INFORMATION', y);
  const v = data.vehicle || {};
  y = addRow('Vehicle', `${v.brand_name || ''} ${v.model_name || ''}`, y);
  y = addRow('Registration No.', v.registration_no || '—', y);
  y = addRow('Vehicle ID', v.vehicle_display_id || '—', y);
  y = addRow('Type', v.vehicle_type || '—', y);
  y = addRow('City', v.city || '—', y);
  y = addLine(y);

  // Comparison data
  const seller = data.sellerData || {};
  const partner = data.partnerData || {};
  const adminEdits = data.adminEdits || {};

  y = addSection('ADMIN VERIFICATION DETAILS', y);
  y = addText('Fields reviewed and corrected by admin:', margin + 2, y, 9, false, [100, 100, 100]);
  y += 5;

  const comparisons = [
    { label: 'KMs Driven', seller: seller.km_run_range, partner: partner.km_run_actual, admin: adminEdits.km_run },
    { label: 'Registration No.', seller: seller.registration_no, partner: partner.plate_verified_reg, admin: adminEdits.registration_no },
    { label: 'Insurance Company', seller: seller.insurance_company, partner: partner.verified_insurance_company, admin: adminEdits.insurance_company },
    { label: 'Insurance Expiry', seller: seller.insurance_expiry, partner: partner.verified_insurance_expiry, admin: adminEdits.insurance_expiry },
    { label: 'PUC Expiry', seller: seller.puc_expiry, partner: partner.verified_puc_expiry, admin: adminEdits.puc_expiry },
  ];

  comparisons.forEach(c => {
    const hasEdit = c.admin && c.admin !== (c.partner || c.seller);
    y = checkPage(y, 12);
    addText(c.label + ':', margin + 2, y, 9, true, [60, 60, 60]);
    addText(`Seller: ${c.seller || '—'}`, margin + 55, y, 8, false, [100, 100, 100]);
    addText(`Partner: ${c.partner || '—'}`, margin + 105, y, 8, false, [50, 100, 200]);
    if (hasEdit) {
      addText(`Admin: ${c.admin}`, margin + 155, y, 8, true, [200, 50, 50]);
    }
    y += 7;
  });
  y = addLine(y);

  // Partner identity check
  y = addSection('PARTNER IDENTITY VERIFICATION', y);
  y = addRow('Partner registered image', 'Compared with selfie at location', y);
  y = addRow('Partner name', data.partnerData?.partner_name || '—', y);
  y = addRow('Identity match result', adminEdits.partner_selfie_match || 'Verified', y);
  y = addLine(y);

  // Admin sign-off
  y = addSection('ADMIN SIGN-OFF', y);
  y = addRow('Reviewed by', data.adminEdits?.reviewed_by || '—', y);
  y = addRow('Review completed', data.adminEdits?.reviewed_at ? new Date(data.adminEdits.reviewed_at).toLocaleString('en-IN') : '—', y);
  y = addRow('Total fields edited', String(data.adminEdits?.editCount || 0), y);
  y = addRow('Final status', 'VEHICLE ADMIN VERIFIED ✓', y, true);

  y += 10;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('This document is auto-generated and stored for 48 hours. X7 Autoz © 2025', margin, y);

  doc.save(`${verificationId || 'verification'}.pdf`);
};

// ─── Image Compare Box ────────────────────────────────────────────────────────
const ImageCompareBox = ({ leftLabel, leftImg, rightLabel, rightImg, matchLabel, matchOk }) => (
  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
    <div className="grid grid-cols-2 gap-3 mb-3">
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase mb-2">{leftLabel}</p>
        {leftImg
          ? <img src={leftImg} alt={leftLabel} className="w-full h-36 object-cover rounded-xl border border-gray-200" />
          : <div className="w-full h-36 bg-gray-200 rounded-xl flex items-center justify-center text-gray-400 text-xs">No image</div>}
      </div>
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase mb-2">{rightLabel}</p>
        {rightImg
          ? <img src={rightImg} alt={rightLabel} className="w-full h-36 object-cover rounded-xl border border-gray-200" />
          : <div className="w-full h-36 bg-gray-200 rounded-xl flex items-center justify-center text-gray-400 text-xs">No image</div>}
      </div>
    </div>
    {matchLabel && (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${matchOk ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        {matchOk ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
        <span className={`text-xs font-medium ${matchOk ? 'text-green-700' : 'text-amber-700'}`}>{matchLabel}</span>
      </div>
    )}
  </div>
);

// ─── Data Row (3-column comparison) ──────────────────────────────────────────
const CompareRow = ({ label, sellerVal, partnerVal, adminVal, adminField, onAdminEdit, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(adminVal || partnerVal || sellerVal || '');
  const hasMismatch = sellerVal && partnerVal && String(sellerVal).trim() !== String(partnerVal).trim();

  const handleSave = () => {
    onAdminEdit(adminField, editVal);
    setEditing(false);
    toast.success(`${label} updated`);
  };

  return (
    <div className={`grid grid-cols-3 gap-3 py-3 border-b border-gray-100 last:border-0 ${hasMismatch ? 'bg-amber-50/40' : ''}`}>
      {/* Col 1: Seller */}
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-800">{sellerVal || '—'}</p>
      </div>
      {/* Col 2: Partner */}
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className={`text-sm font-medium ${hasMismatch ? 'text-amber-700 font-bold' : 'text-gray-800'}`}>
          {partnerVal || '—'}
          {hasMismatch && <span className="ml-1 text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full">Mismatch</span>}
        </p>
      </div>
      {/* Col 3: Admin */}
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        {editing ? (
          <div className="flex gap-1.5">
            <input type="text" value={editVal} onChange={e => setEditVal(e.target.value)}
              className="flex-1 border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <button onClick={handleSave} className="w-7 h-7 bg-green-500 text-white rounded-lg flex items-center justify-center">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setEditing(false)} className="w-7 h-7 bg-gray-200 text-gray-600 rounded-lg flex items-center justify-center">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium flex-1 ${adminVal ? 'text-blue-700 font-bold' : 'text-gray-500'}`}>
              {adminVal || partnerVal || '—'}
            </p>
            {editable && (
              <button onClick={() => { setEditVal(adminVal || partnerVal || sellerVal || ''); setEditing(true); }}
                className="w-6 h-6 bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 rounded-lg flex items-center justify-center transition">
                <Edit3 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN ADMIN VERIFICATION REVIEW ====================
const AdminVerificationReview = () => {
  const { token } = useAuth();
  const { user } = useAuth();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const [reviewing, setReviewing] = useState(null); // { vehicle, inspection, partnerUser }
  const [adminEdits, setAdminEdits] = useState({});
  const [finishing, setFinishing] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => { fetchSubmissions(); }, [page]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/partner-inspection-submissions?skip=${(page - 1) * ITEMS_PER_PAGE}&limit=${ITEMS_PER_PAGE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(res.data.submissions || []);
      setTotal(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / ITEMS_PER_PAGE));
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openReview = async (vehicleId) => {
    try {
      const [vehicleRes, inspRes] = await Promise.all([
        axios.get(`${API}/vehicles/${vehicleId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vehicles/${vehicleId}/inspection`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const vehicle = vehicleRes.data.vehicle;
      const inspection = inspRes.data.inspection;

      // Load partner user info (for photo)
      let partnerUser = null;
      if (inspection?.partner_id) {
        try {
          const pRes = await axios.get(`${API}/superadmin/partners/${inspection.partner_id}/profile`, { headers: { Authorization: `Bearer ${token}` } });
          partnerUser = pRes.data.partner;
        } catch {}
      }

      setReviewing({ vehicle, inspection, partnerUser });
      setAdminEdits(inspection?.admin_verified_data || {});
    } catch {
      toast.error('Failed to load inspection details');
    }
  };

  const handleAdminEdit = (field, value) => {
    setAdminEdits(prev => {
      const updated = { ...prev, [field]: value };
      // Count edits
      updated.editCount = Object.keys(updated).filter(k => !['editCount', 'reviewed_by', 'reviewed_at', 'admin_verified', 'partner_selfie_match'].includes(k)).length;
      return updated;
    });
  };

  const handleFinishVerification = async () => {
    if (!reviewing) return;
    setFinishing(true);

    const vehicleId = reviewing.vehicle.id;
    const verificationId = reviewing.inspection?.checklist_data?.verification_id || reviewing.vehicle.verification_id || `VER-${vehicleId.slice(0, 8)}`;
    const editCount = adminEdits.editCount || 0;

    try {
      // Save admin verified data
      const reviewedData = {
        ...adminEdits,
        reviewed_by: user?.name || 'Admin',
        reviewed_at: new Date().toISOString(),
        admin_verified: true,
        editCount,
      };

      await axios.put(`${API}/admin/inspection/${vehicleId}/admin-verify`, {
        admin_verified_data: reviewedData,
        admin_edit_count: editCount,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Log edits in master data (BadeSahab)
      if (editCount > 0) {
        await axios.post(`${API}/superadmin/admin-edit-logs`, {
          admin_id: user?.id,
          admin_name: user?.name,
          vehicle_id: vehicleId,
          verification_id: verificationId,
          edit_count: editCount,
          edits: adminEdits,
          edited_at: new Date().toISOString(),
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }

      // Store in localStorage with 48-hour expiry
      const storageKey = `x7_admin_verification_${vehicleId}`;
      const storageData = {
        vehicleId,
        verificationId,
        vehicle: reviewing.vehicle,
        inspection: reviewing.inspection,
        adminEdits: reviewedData,
        storedAt: Date.now(),
        expiresAt: Date.now() + 48 * 60 * 60 * 1000, // 48 hours
      };
      localStorage.setItem(storageKey, JSON.stringify(storageData));

      toast.success('Vehicle admin verification complete!');

      // Auto-download PDF
      setDownloadingPDF(true);
      try {
        await generateVerificationPDF({
          vehicle: reviewing.vehicle,
          sellerData: {
            km_run_range: reviewing.vehicle.km_run_range,
            registration_no: reviewing.vehicle.registration_no,
            insurance_company: reviewing.vehicle.insurance_company,
            insurance_expiry: reviewing.vehicle.insurance_expiry,
            puc_expiry: reviewing.vehicle.puc_expiry,
          },
          partnerData: {
            km_run_actual: reviewing.inspection?.checklist_data?.km_run_actual,
            plate_verified_reg: reviewing.vehicle.registration_no,
            verified_insurance_company: reviewing.inspection?.checklist_data?.insurance_puc_verification?.verified_insurance_company,
            verified_insurance_expiry: reviewing.inspection?.checklist_data?.insurance_puc_verification?.verified_insurance_expiry,
            verified_puc_expiry: reviewing.inspection?.checklist_data?.insurance_puc_verification?.verified_puc_expiry,
            partner_name: reviewing.partnerUser?.name,
          },
          adminEdits: reviewedData,
        }, vehicleId, verificationId);
      } catch (pdfErr) {
        toast.error('PDF download failed — data saved successfully');
      } finally {
        setDownloadingPDF(false);
      }

      setReviewing(null);
      fetchSubmissions();
    } catch {
      toast.error('Failed to complete verification');
    } finally {
      setFinishing(false);
    }
  };

  // Clean up expired localStorage entries
  useEffect(() => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('x7_admin_verification_'));
    keys.forEach(k => {
      try {
        const data = JSON.parse(localStorage.getItem(k));
        if (data.expiresAt && Date.now() > data.expiresAt) {
          localStorage.removeItem(k);
        }
      } catch { localStorage.removeItem(k); }
    });
  }, []);

  // ─── Review Panel ──────────────────────────────────────────────────────────
  if (reviewing) {
    const { vehicle, inspection, partnerUser } = reviewing;
    const cd = inspection?.checklist_data || {};
    const ipv = cd.insurance_puc_verification || {};
    const vehiclePhotos = cd.vehicle_photos || {};

    const sellerData = {
      km_run_range: vehicle.km_run_range,
      registration_no: vehicle.registration_no,
      insurance_company: vehicle.insurance_company || '—',
      insurance_expiry: vehicle.insurance_expiry || '—',
      puc_expiry: vehicle.puc_expiry || '—',
      has_insurance: vehicle.has_insurance,
      has_puc: vehicle.has_puc,
    };

    const partnerData = {
      km_run_actual: cd.km_run_actual || cd.form_answers?.['0'] || '—',
      verified_insurance_company: ipv.verified_insurance_company || '—',
      verified_insurance_expiry: ipv.verified_insurance_expiry || '—',
      verified_puc_expiry: ipv.verified_puc_expiry || '—',
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => setReviewing(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ChevronLeft className="h-5 w-5" /> Back to Submissions
          </button>
          <div className="text-center">
            <h2 className="text-lg font-bold text-gray-900">Admin Verification Review</h2>
            <p className="text-sm text-gray-500">{vehicle.brand_name} {vehicle.model_name} · {vehicle.registration_no}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleFinishVerification}
              disabled={finishing || downloadingPDF}
              className="bg-green-600 hover:bg-green-700 text-white gap-2">
              {finishing || downloadingPDF
                ? <><Loader2 className="h-4 w-4 animate-spin" />{downloadingPDF ? 'Generating PDF...' : 'Finishing...'}</>
                : <><CheckCircle2 className="h-4 w-4" />Finish Vehicle Admin Verification</>}
            </Button>
          </div>
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2 text-center">
            <p className="text-xs font-bold text-orange-700 uppercase">Column 1 — Seller Data</p>
            <p className="text-xs text-orange-500">Submitted during vehicle listing</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 text-center">
            <p className="text-xs font-bold text-blue-700 uppercase">Column 2 — Partner Data</p>
            <p className="text-xs text-blue-500">Submitted after physical inspection</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2 text-center">
            <p className="text-xs font-bold text-green-700 uppercase">Column 3 — Admin Verified</p>
            <p className="text-xs text-green-500">Final verified data (editable)</p>
          </div>
        </div>

        {/* ── SECTION 1: Partner Identity Comparison ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <User className="h-4 w-4 text-blue-500" /> 1. Partner Identity Verification
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Partner's Registered Photo</p>
              {partnerUser?.photo
                ? <img src={partnerUser.photo} alt="Partner registered" className="w-full h-48 object-cover rounded-xl border-2 border-blue-200" />
                : <div className="w-full h-48 bg-blue-50 rounded-xl border-2 border-blue-200 flex flex-col items-center justify-center text-blue-400">
                    <User className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-xs">No registered photo</p>
                    <p className="text-xs opacity-60 mt-1">Upload partner photo during registration</p>
                  </div>}
              {partnerUser && <p className="text-xs font-medium text-gray-600 mt-2 text-center">{partnerUser.name} · {partnerUser.partner_uid}</p>}
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Selfie at Inspection Location</p>
              {inspection?.arrival_photo
                ? <img src={inspection.arrival_photo} alt="Partner selfie" className="w-full h-48 object-cover rounded-xl border-2 border-orange-200" />
                : <div className="w-full h-48 bg-orange-50 rounded-xl border-2 border-orange-200 flex flex-col items-center justify-center text-orange-400">
                    <Camera className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-xs">No selfie uploaded</p>
                  </div>}
              <p className="text-xs text-gray-500 mt-2 text-center">Taken at inspection location</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <p className="text-sm font-medium text-gray-700">Identity Match:</p>
            {['Verified ✓', 'Mismatch — Flag'].map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="selfie_match" value={opt}
                  checked={adminEdits.partner_selfie_match === opt}
                  onChange={() => handleAdminEdit('partner_selfie_match', opt)}
                  className="accent-blue-500" />
                <span className={`text-sm font-medium ${opt.includes('✓') ? 'text-green-700' : 'text-red-700'}`}>{opt}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ── SECTION 2: Vehicle Image Comparison ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Car className="h-4 w-4 text-blue-500" /> 2. Vehicle Image Comparison
          </h3>
          <ImageCompareBox
            leftLabel="Seller's Vehicle Photo"
            leftImg={vehicle.images?.[0]}
            rightLabel="Partner's Vehicle Photo (at inspection)"
            rightImg={vehiclePhotos.full || inspection?.with_vehicle_photo}
            matchLabel="Review both images — ensure same vehicle"
          />
        </div>

        {/* ── SECTION 3: KMs Driven & Odometer ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Gauge className="h-4 w-4 text-blue-500" /> 3. KMs Driven Verification
          </h3>
          <ImageCompareBox
            leftLabel="Seller's Declared KMs"
            rightLabel="Partner's Odometer Photo"
            rightImg={vehiclePhotos.odometer}
            matchLabel="Compare KMs on screen vs seller's declaration"
          />
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                <p className="text-xs text-orange-600 font-bold uppercase mb-1">Seller Said</p>
                <p className="text-lg font-bold text-gray-800">{vehicle.km_run_range} KM</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-bold uppercase mb-1">Partner Recorded</p>
                <p className="text-lg font-bold text-gray-800">{partnerData.km_run_actual}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                <p className="text-xs text-green-600 font-bold uppercase mb-1">Admin Verified (edit if needed)</p>
                <Input
                  value={adminEdits.km_run || partnerData.km_run_actual || vehicle.km_run_range || ''}
                  onChange={e => handleAdminEdit('km_run', e.target.value)}
                  placeholder="Enter actual KMs"
                  className="h-9 text-sm font-bold"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── SECTION 4: Number Plate ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Hash className="h-4 w-4 text-blue-500" /> 4. Registration Number Verification
          </h3>
          <ImageCompareBox
            leftLabel="Seller's Declared Reg. No."
            rightLabel="Partner's Number Plate Photo"
            rightImg={vehiclePhotos.plate}
            matchLabel="Verify reg. number matches plate in photo"
          />
          <div className="mt-4 grid grid-cols-3 gap-3 items-end">
            <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
              <p className="text-xs text-orange-600 font-bold uppercase mb-1">Seller Said</p>
              <p className="text-base font-bold font-mono text-gray-800">{vehicle.registration_no}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-xs text-blue-600 font-bold uppercase mb-1">As Seen in Photo</p>
              <p className="text-base font-bold font-mono text-gray-800">{vehicle.registration_no}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 border border-green-100">
              <p className="text-xs text-green-600 font-bold uppercase mb-1">Admin Verified</p>
              <Input
                value={adminEdits.registration_no || vehicle.registration_no || ''}
                onChange={e => handleAdminEdit('registration_no', e.target.value.toUpperCase())}
                placeholder="Correct reg. no."
                className="h-9 text-sm font-bold font-mono"
              />
            </div>
          </div>
        </div>

        {/* ── SECTION 5: Insurance ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-500" /> 5. Insurance Verification
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Insurance Document Photo (by partner)</p>
              {ipv.insurance_photo
                ? <img src={ipv.insurance_photo} alt="Insurance doc" className="w-full h-44 object-cover rounded-xl border" />
                : <div className="w-full h-44 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-xs border-dashed border-2">No insurance photo</div>}
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Seller said: Has Insurance</p>
                <p className={`text-sm font-bold ${vehicle.has_insurance ? 'text-blue-700' : 'text-red-600'}`}>
                  {vehicle.has_insurance ? '✅ Yes' : '❌ No'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Seller said: Company</p>
                <p className="text-sm font-bold text-gray-800">{vehicle.insurance_company || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Seller said: Expiry</p>
                <p className="text-sm font-bold text-gray-800">{vehicle.insurance_expiry || '—'}</p>
              </div>
            </div>
          </div>

          {/* 3-col comparison for insurance */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-0">
            <div className="grid grid-cols-3 gap-3 pb-2 border-b border-gray-100">
              <p className="text-xs font-bold text-orange-600 uppercase">Seller Data</p>
              <p className="text-xs font-bold text-blue-600 uppercase">Partner Verified</p>
              <p className="text-xs font-bold text-green-600 uppercase">Admin Verified</p>
            </div>
            <CompareRow label="Company"
              sellerVal={vehicle.insurance_company}
              partnerVal={ipv.verified_insurance_company}
              adminVal={adminEdits.insurance_company}
              adminField="insurance_company"
              onAdminEdit={handleAdminEdit}
            />
            <CompareRow label="Expiry Date"
              sellerVal={vehicle.insurance_expiry}
              partnerVal={ipv.verified_insurance_expiry}
              adminVal={adminEdits.insurance_expiry}
              adminField="insurance_expiry"
              onAdminEdit={handleAdminEdit}
            />
          </div>
        </div>

        {/* ── SECTION 6: PUC ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-green-500" /> 6. PUC Certificate Verification
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">PUC Certificate Photo (by partner)</p>
              {ipv.puc_photo
                ? <img src={ipv.puc_photo} alt="PUC cert" className="w-full h-44 object-cover rounded-xl border" />
                : <div className="w-full h-44 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-xs border-dashed border-2">No PUC photo</div>}
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Seller said: Has PUC</p>
                <p className={`text-sm font-bold ${vehicle.has_puc ? 'text-green-700' : 'text-red-600'}`}>
                  {vehicle.has_puc ? '✅ Yes' : '❌ No'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Seller said: Expiry</p>
                <p className="text-sm font-bold text-gray-800">{vehicle.puc_expiry || '—'}</p>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-4 space-y-0">
            <div className="grid grid-cols-3 gap-3 pb-2 border-b border-gray-100">
              <p className="text-xs font-bold text-orange-600 uppercase">Seller Data</p>
              <p className="text-xs font-bold text-blue-600 uppercase">Partner Verified</p>
              <p className="text-xs font-bold text-green-600 uppercase">Admin Verified</p>
            </div>
            <CompareRow label="PUC Expiry"
              sellerVal={vehicle.puc_expiry}
              partnerVal={ipv.verified_puc_expiry}
              adminVal={adminEdits.puc_expiry}
              adminField="puc_expiry"
              onAdminEdit={handleAdminEdit}
            />
          </div>
        </div>

        {/* ── SECTION 7: Owner Photo ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4 flex items-center gap-2">
            <User className="h-4 w-4 text-blue-500" /> 7. Vehicle Owner Verification
          </h3>
          <p className="text-xs text-gray-500 mb-3">Verify the registration number / vehicle with the owner standing next to it.</p>
          {inspection?.with_owner_photo
            ? <img src={inspection.with_owner_photo} alt="Owner with vehicle" className="w-full max-h-64 object-cover rounded-xl border" />
            : <div className="w-full h-40 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-xs border-dashed border-2">No owner photo</div>}
          <div className="mt-3 flex items-center gap-3">
            <p className="text-sm font-medium text-gray-700">Owner Verification:</p>
            {['Confirmed ✓', 'Could not verify'].map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="owner_verify" value={opt}
                  checked={adminEdits.owner_verification === opt}
                  onChange={() => handleAdminEdit('owner_verification', opt)}
                  className="accent-green-500" />
                <span className={`text-sm font-medium ${opt.includes('✓') ? 'text-green-700' : 'text-amber-700'}`}>{opt}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Admin Notes */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Edit3 className="h-4 w-4" /> Admin Notes
          </h3>
          <textarea rows={3}
            value={adminEdits.admin_notes || ''}
            onChange={e => handleAdminEdit('admin_notes', e.target.value)}
            placeholder="Any additional notes about this verification..."
            className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
          />
          {adminEdits.editCount > 0 && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <Info className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700 font-medium">{adminEdits.editCount} field(s) edited — will be logged in Master Data</p>
            </div>
          )}
        </div>

        {/* Finish button (bottom) */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col items-center gap-3">
          <p className="text-sm text-gray-500 text-center">Clicking the button below will mark this verification as complete, auto-download the PDF report, and store data for 48 hours.</p>
          <Button
            onClick={handleFinishVerification}
            disabled={finishing || downloadingPDF}
            className="w-full max-w-sm bg-green-600 hover:bg-green-700 text-white py-4 text-base font-bold rounded-2xl gap-2">
            {finishing || downloadingPDF
              ? <><Loader2 className="h-5 w-5 animate-spin" />{downloadingPDF ? 'Generating PDF Report...' : 'Saving...'}</>
              : <><Download className="h-5 w-5" />Finish Vehicle Admin Verification & Download PDF</>}
          </Button>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Partner Inspection Submissions</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} total submissions · Review each form before finalizing</p>
        </div>
        <button onClick={fetchSubmissions} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-3 py-2 bg-gray-100 rounded-xl">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <Shield className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No inspection submissions yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {submissions.map((sub, i) => (
              <div key={sub.vehicle_id || i} className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-blue-200 transition">
                <div className="flex items-center gap-4">
                  {sub.images?.[0] && <img src={sub.images[0]} alt="vehicle" className="w-20 h-16 object-cover rounded-xl flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="font-bold text-gray-900">{sub.brand_name} {sub.model_name}</p>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Inspection Submitted</span>
                      {sub.admin_verified_data?.admin_verified && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Admin Verified</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 font-mono">{sub.registration_no}</p>
                    {sub.vehicle_display_id && <p className="text-xs text-blue-600 font-mono">{sub.vehicle_display_id}</p>}
                    <p className="text-xs text-gray-400 mt-1">{sub.city} · Verified by partner</p>
                  </div>
                  <Button
                    onClick={() => openReview(sub.vehicle_id || sub.id)}
                    className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white gap-2">
                    <Eye className="h-4 w-4" /> Review
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition">
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
                return (
                  <button key={pageNum} onClick={() => setPage(pageNum)}
                    className={`w-9 h-9 rounded-xl text-sm font-medium transition ${page === pageNum ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition">
                <ChevronRight className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminVerificationReview;